import { debounceTime } from "rxjs";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  ApplicationRef,
  ElementRef,
  Injector,
  NgZone,
  ViewChild,
  OnDestroy,
  EventEmitter,
  forwardRef,
  Input,
  AfterViewInit,
  Output,
  Component,
} from "@angular/core";

import { BfxBaseBlotterService } from "./bfx-base-blotter.service";
import { GoldenLayoutService } from "../golden-layout/golden-layout-service";
import { GoldenLayoutComponent } from "../golden-layout/golden-layout-component";
import { TranslateService } from "@ngx-translate/core";
import { BfxBlotterFilterService } from "../bfx-blotter-filter/bfx-blotter-filter.service";
import { BfxBlotterFilter, FilterType } from "../bfx-blotter-filter/bfx-blotter-filter.constants";
import { BfxBlotterFilterBarComponent } from "../bfx-blotter-filter/bfx-blotter-filter-bar.component";
import { BfxBlotterFilterable } from "../bfx-blotter-filter/bfx-blotter-filterable";
import { BfxBlotterFilterManagerComponent } from "../bfx-blotter-filter/bfx-blotter-filter-manager.component";
import { BfxBaseBlotterFilteringService } from "./bfx-base-blotter-filtering.service";
import { BfxBaseBlotterContextMenuService } from "./bfx-base-blotter-contextmenu.service";
import {
  BaseTableDefinitionFactory,
  TableDefinition,
  IndexType,
  SystemConfigPromiseService,
  ProfilePromiseActionService,
  ProfileSettingKeys,
  GenericTooltipValueGetter,
  PricingService,
} from "shared/cubiq-services";
import { FixingDateSCHelper } from "./../../shared/cubiq-services/model/orders/FixingDateSCHelper";
// import { BfxVwapOrdersService } from "../bfx-vwap/bfx-vwap-orders.service";
import {
  GridOptions,
  RowNode,
  ColumnApi,
  CellMouseOverEvent,
  CellEvent,
  CellClassParams,
  ColDef,
  ILoadingOverlayComp,
  CellContextMenuEvent,
} from "ag-grid-community/main";

import { BfxBaseBlotterColumnSelectorService } from "../bfx-base-blotter/bfx-base-blotter-columnselector.service";

import { BfxMouseModifierKeyService } from "app/bfx-common/services/bfx-mouse-modifier-key.service";

import { BfxUserProfileService } from "../bfx-common/services/bfx-user-profile.service";

const DEFAULT_CELL_CLASS: string = "ag-cell-default";

const MOUSEOVER_SHOW_TOOLTIP_TIMEOUT = 200;

export const DEFAULT_ROW_HEIGHT: number = 24;
export const FULL_WIDTH_HEIGHT: number = 8;

export function provideBlotter(component: any) {
  return { provide: BfxBaseBlotter, useExisting: forwardRef(() => component) };
}

/* Example


@Component({
	selector: 'bfx-blotter-orders',
	encapsulation: ViewEncapsulation.None
})*/
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import {
  BfxSaveProfileNotificationService,
  PROFILE_TYPE,
} from "../bfx-common/services/bfx-save-profile-notification.service";

import { AgGridCellExport } from "../shared/ag-grid/AgGridCellExport";
import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";
import { COLUMN_SELECTION } from "shared/cubiq-services/model/TableDefinitionFactory.constants";
import { BfxColDef } from "shared/cubiq-services/model/TableDefinition";
import { Subscription, Subject } from "rxjs";
import { COLUMN_ORDER_FIXING_DATE } from "shared/cubiq-services/model/OrdersTableDefinitionFactory.constants";
import { getPath } from "../../shared/helper-services/dom-path";
import { BFXDomHelper } from "app/bfx-common/helpers/bfx-dom-helper";
import { FilterConfiguration } from "../bfx-blotter-filter/FilterConfiguration";
import {
  SORT_INSTRUMENT,
  SORT_LADDER_TRACKING,
  SORT_PERC_AWAY,
} from "./bfx-base-blotter.constants";
const CELL_DOUBLE_CLICK_THRESHOLD = 300;

export const ROW_SELECTION_MARKER: string = "bfx-range-selection";
export const ROW_SELECTION_FIRST_CLASS: string = "first";
export const ROW_SELECTION_LAST_CLASS: string = "last";

export class BaseBlotterOverlayComponent implements ILoadingOverlayComp {
  private eGui = null;

  init(params) {
    this.eGui = document.createElement("div");
    this.eGui.innerHTML = `
      <div>
          <i class="fa icon_ion_loading fa-spin"></i>
              <span>Loading ...</span>
          </div>
      `;
  }

  getGui() {
    return this.eGui;
  }
}

@Component({
  selector: "bfx-base-blotter",
  template: "",
  viewProviders: [BfxBaseBlotterColumnSelectorService],
  standalone: false,
})
export abstract class BfxBaseBlotter
  implements OnDestroy, GoldenLayoutComponent, BfxBlotterFilterable, AfterViewInit
{
  public static instanceId = 0;

  public blotterId: string;
  public rowHeight = DEFAULT_ROW_HEIGHT;
  public height: number = 500;

  protected primed: boolean = false;
  protected embeddedTablePreloadData: any = null;

  public filterConfiguration: FilterConfiguration = null;
  public columnsConfiguration: any = {};
  public sortingModel: any = null;
  public defaultSortingModel: any = null;
  public blotterFilterBar: BfxBlotterFilterBarComponent = null;
  public blotterInstance: any = null;

  public tableDefinition: TableDefinition = null;
  public columnDefs: Array<BfxColDef> = [];

  public gridOptions: GridOptions;
  public gridColumnApi: ColumnApi;

  public showSearch: boolean = false;
  public searchValue: string;

  private _embeddedTableMode: boolean = false;

  protected mouseOverCellEvent: CellEvent = null;
  protected nodeMouseUpCountMap: WeakMap<RowNode, number> = new WeakMap();

  public get embeddedTableMode() {
    return this._embeddedTableMode;
  }

  public set embeddedTableMode(value) {
    if (
      this.gridOptions != null &&
      this.gridOptions.context != null &&
      typeof this.gridOptions.context === "object"
    ) {
      this.gridOptions.context["embeddedTableMode"] = value;
    }

    this._embeddedTableMode = value;
  }

  public embeddedTableModeContextMenu: boolean = false;
  @Input()
  public embeddedTableModeSuppressSelection: boolean = true;

  private myHeaderSelectionTimer: any;

  protected fullUpdateSubscription: Subscription;
  protected forceUpdateSubscription: Subscription;
  protected deleteSubscription: Subscription;

  private doingDragSelect: boolean = false;

  get isDoingDragSelect() {
    return this.doingDragSelect;
  }

  public pendingFinishActions: Set<RowNode> = new Set();

  private firstRowToSelect: any;
  private lastRowToSelect: any;
  protected selectedIdsBeforeDrag: Array<string> = [];
  public selectedIdsCount: number = 0;
  protected suppressNextFilterChangedDeselection = false;

  protected subjectRefreshHeader: Subject<void> = new Subject<void>();
  protected embeddedTablePreloadCompletedSubject: Subject<void> = new Subject<void>();
  public embeddedTablePreloadCompleted$ = this.embeddedTablePreloadCompletedSubject.asObservable();

  public viewInitComplete: boolean = false; // for forwarding the resize event

  private _showRowColors: boolean = true;
  protected myFixingDateHelper: FixingDateSCHelper;

  @Input()
  public get showRowColors(): boolean {
    return this._showRowColors;
  }
  public set showRowColors(value: boolean) {
    if (this._showRowColors === value) {
      return;
    }

    this._showRowColors = value;
    if (this.gridOptions) {
      this.gridOptions.rowClassRules = {};
    }
  }

  @Input()
  public embeddedHideUnconfigured = true;

  @Input()
  public embeddedContextMenuService: BfxBaseBlotterContextMenuService<any> = null;

  protected scheduledRefresh: NodeJS.Timeout = null;
  public blotterView: ElementRef;
  public search: EventEmitter<string | void>;
  @ViewChild("topMenu") topMenuView: ElementRef;
  @ViewChild("tableTarget") tableTargetView: ElementRef;

  @Input("tableDefinitionFactory")
  protected _tableDefinitionFactory: BaseTableDefinitionFactory = null;

  protected gridDataUpdatedSubject = new Subject<{
    displayedRowCount: number;
    rowCount: number;
  }>();

  public gridDataUpdated$ = this.gridDataUpdatedSubject.asObservable();

  @Input("publishGridDataUpdateInfo")
  public publishGridDataUpdateInfo: boolean = false;

  @Output()
  public gridReady: EventEmitter<any> = new EventEmitter();

  constructor(
    public BfxBlotterElement: ElementRef,
    applicationRef: ApplicationRef,
    injector: Injector,
    public service: BfxBaseBlotterService,
    private _contextMenuService: BfxBaseBlotterContextMenuService<any>,
    zone: NgZone,
    factory: BaseTableDefinitionFactory,
    protected layoutService: GoldenLayoutService,
    protected translationService: TranslateService,
    protected blotterFilterService: BfxBlotterFilterService,
    protected filteringService: BfxBaseBlotterFilteringService,
    protected columnSelectorService: BfxBaseBlotterColumnSelectorService,
    protected profileActionService: ProfilePromiseActionService,
    protected sysConfigService: SystemConfigPromiseService,
    protected mouseModifierKeyService: BfxMouseModifierKeyService,
    protected saveProfileNotificationService: BfxSaveProfileNotificationService,
    protected userProfileService: BfxUserProfileService,
  ) {
    (this.constructor as any).instanceId++;
    this.blotterId = this.constructor.name + "_" + (this.constructor as any).instanceId;
    this.blotterInstance = this;
    this.blotterView = BfxBlotterElement;

    if (this._tableDefinitionFactory == null) {
      this._tableDefinitionFactory = factory;
    }

    this.gridOptions = this.createsGridOptions();

    this.gridOptions.suppressBrowserResizeObserver =
      NativeDesktopSupport.isNativeDesktopContainer();

    this.addCustomOverlay();

    this.gridOptions.cacheQuickFilter = false;

    NativeDesktopSupport.prepareGridOptions(this.gridOptions, BfxBlotterElement.nativeElement);
    this.subscribeToSearch();

    this.subjectRefreshHeader
      .pipe(debounceTime(100))
      .subscribe(this.updateSelectionHeader.bind(this));

    this.myFixingDateHelper = new FixingDateSCHelper();
  }

  protected addCustomOverlay(): void {
    this.gridOptions.loadingOverlayComponent = BaseBlotterOverlayComponent;
  }

  protected createsGridOptions(): GridOptions {
    let funcGetRowNodId = function (data) {
      if (data && this.tableDefinition != null)
        return String(data[this.tableDefinition.primaryKey]);
      return "";
    }.bind(this);

    // Scrollbar width is taken from handle css variable
    const scrollBarWidth = BFXDomHelper.getVariablePx(
      document,
      "--ds-comp-scrollbar-handle-container-sizing-width",
      10,
    );

    return <GridOptions>{
      scrollbarWidth: scrollBarWidth,
      getRowNodeId: funcGetRowNodId,
      rowSelection: "multiple",
      deltaRowDataMode: true,
      valueCache: true,
      suppressContextMenu: true,
      headerHeight: 24,
      suppressColumnMoveAnimation: true,
      suppressDragLeaveHidesColumns: true,
      suppressScrollOnNewData: true,
      suppressFocusAfterRefresh: true,
      suppressAutoSize: true,
      cacheQuickFilter: true,
      rowMultiSelectWithClick: false,
      showToolPanel: false,
      toolPanelSuppressSideButtons: true,
      enableFilter: true,
      rowDeselection: true,
      enableBrowserTooltips: true,
      embedFullWidthRows: true,
      isFullWidthCell: function (rowNode) {
        // market bar
        if (rowNode != null && rowNode.data != null && rowNode.data.fullWidth) {
          return true;
        }
        // normal order
        return false;
      },
      getRowHeight: function (params) {
        // market bar
        if (
          params != null &&
          params.node != null &&
          params.node.data != null &&
          params.node.data.fullWidth
        ) {
          return FULL_WIDTH_HEIGHT;
        } else {
          // normal order
          return DEFAULT_ROW_HEIGHT;
        }
      },
      fullWidthCellRenderer: function () {
        return "<div></div>";
      },
      clipboardDeliminator: ",",
      processCellForClipboard: AgGridCellExport,
      context: this.initGridOptionsContext(),
      onGridReady: (params) => {
        if (this.gridOptions != null) {
          this.gridOptions.api = params.api;
          this.gridOptions.columnApi = params.columnApi;
          this.gridColumnApi = params.columnApi;

          // rest of initialization happens after ag-grid is ready
          if (this.gridOptions.api != null) {
            this.gridOptions.api.showLoadingOverlay();
            //hide the no rows message
            this.gridOptions.suppressNoRowsOverlay = true;
            setTimeout(() => this.initializeInMemoryCompleted(), 0);
          }
        }

        this._tableDefinitionFactory.defineTable(this.getTableDefinitionContext()).then(
          (value) => {
            this.tableDefinition = value;
            this.initializeColumnDefinitions();

            if (this.embeddedTableMode) {
              // load a static data set as per initializeInMemory()
              if (this.embeddedTablePreloadData != null) {
                this.setEmbeddedTable(
                  this.embeddedTablePreloadData.rowData,
                  this.embeddedTablePreloadData.config,
                  this.embeddedTablePreloadData.allowListOverrideConfig,
                  this.embeddedTablePreloadData.checkPrimaryPriority,
                );

                this.embeddedTablePreloadData = null;
                this.embeddedTablePreloadCompletedSubject.next();
              }
              // TODO consider an opt-in default this.setEmbeddedTable([]);
            } else {
              this.initializeInMemory();
            }

            try {
              // this should be removed when upgrading to ag-grid@23.0.0+
              (
                params.api as any
              ).context.beanWrappers.tooltipManager.beanInstance.MOUSEOVER_SHOW_TOOLTIP_TIMEOUT =
                MOUSEOVER_SHOW_TOOLTIP_TIMEOUT;
            } catch (e) {
              //
              logger.out.warn(
                `${this.constructor.name} onGridReady(%O) failed to override MOUSEOVER_SHOW_TOOLTIP_TIMEOUT`,
                e,
              );
            }

            this.gridReady.emit(this.gridOptions);
          },
          (reason: string) => {
            logger.out.info(
              "RawDataOrderAdapter:_tableDefinitionFactory.defineTabl rejection: " + reason,
            );
          },
        );
      },
      navigateToNextCell: () => {
        return null;
      },
      onCellMouseOver: (event: CellMouseOverEvent) => {
        if (this.mouseOverCellEvent !== null && this.mouseOverCellEvent.node !== event.node) {
          this.nodeMouseUpCountMap.delete(this.mouseOverCellEvent.node);
        }
        this.mouseOverCellEvent = event;

        setImmediate(() => {
          if (this.doingDragSelect) {
            if (event.event["buttons"] == 0) {
              this.doingDragSelect = false;
            } else {
              this.lastRowToSelect = event.rowIndex;
              // event.node.setSelected(true);
              this.gridOptions.api.deselectAll();
              this.selectedIdsBeforeDrag.forEach((element) => {
                let row = this.gridOptions.api.getRowNode(element);
                if (row && row.isSelected() == false) {
                  row.setSelected(true, false, true);
                }
              });
              for (
                let index = Math.min(this.firstRowToSelect, this.lastRowToSelect);
                index <= Math.max(this.firstRowToSelect, this.lastRowToSelect);
                index++
              ) {
                let row = this.gridOptions.api.getDisplayedRowAtIndex(index);
                if (row && (row.isSelected() == false || index === event.rowIndex)) {
                  row.setSelected(true, false, index !== event.rowIndex);

                  if (this.hasPendingFinishAction(row)) {
                    this.pendingFinishActions.add(row);
                  }
                }
                // this.gridOptions.api.selectIndex(index,true,false);
              }
            }
          }
        });
      },

      onCellMouseDown: (...events) => {
        // only handle after eventloop- forces mouseover logic to run after mousedown
        setImmediate(() => {
          this.onCellMouseDown.call(this, ...events);
        });
      },

      // for tooltipComponents
      tooltipShowDelay: 0,
    };
  }

  /**
   * MUST NOT DO:
   *
   * {
   * ...
   * blotter: this,
   * ...
   * }
   *
   * _context must not be use to mutate state of ag-grid_
   *
   * _context must be only used to read properties and act accordingly_
   *
   *
   * Safe use: for constant properties:
   *
   * {
   * ...
   * embeddedTableMode: this.embeddedTableMode,
   * ...
   * }
   *
   * Safe use: for dynamic properties
   * (must be side effect free/read-only/immutable)
   *
   * {
   * ..
   * this.getDynamicProperty()
   * ...
   * }
   *
   * it is fine for bfx-base-blotter to change a property
   * of context object returned here once it's paired
   * with a _single_ appropriate refresh!
   */
  protected initGridOptionsContext(): any {
    return {
      embeddedTableMode: this.embeddedTableMode,
    };
  }

  protected onCellMouseDown(event: any): void {
    // Handler is only for selection so if selection is disabled, abort
    if (this.gridOptions.rowSelection == "none") {
      return;
    }

    if (
      event != null &&
      event.event != null &&
      event.event.target != null &&
      event.event.target instanceof HTMLElement &&
      event.event.target.classList.contains("no-cell-mouse-down")
    ) {
      if (event != null && event.node instanceof RowNode)
        this.suppressNextFilterChangedDeselection = true;
      return;
    }

    this.doingDragSelect = true;

    this.firstRowToSelect = event.rowIndex;
    this.lastRowToSelect = event.rowIndex;

    // There is no need to listen to each document for a mouse event if we have it here
    if (NativeDesktopSupport.isNativeDesktopContainer() && event?.event != null) {
      this.mouseModifierKeyService.onCtrlMouseDown(event.event);
    }

    if (
      event.node.isSelected() &&
      this.selectedRowsLength() === 1 &&
      !this.mouseModifierKeyService.ctrlKey() &&
      !this.mouseModifierKeyService.shiftKey() &&
      this.mouseModifierKeyService.isMouseLeft() &&
      !this.mouseModifierKeyService.isTargetCheckbox()
    ) {
      // setTimeout((node)=>{
      // 	node.setSelected(false);
      // },200,event.node);
    } else if (
      !this.mouseModifierKeyService.ctrlKey() &&
      !this.mouseModifierKeyService.shiftKey() &&
      this.mouseModifierKeyService.isMouseLeft() &&
      !this.mouseModifierKeyService.isTargetCheckbox() &&
      (event.node.data == null || !event.node.data.isLadderRate) &&
      !this.mouseModifierKeyService.isTargetDownCheckbox()
    ) {
      this.gridOptions.api.deselectAll();
    }

    this.selectedIdsBeforeDrag = [];
    this.gridOptions.api.getSelectedNodes().forEach((element) => {
      this.selectedIdsBeforeDrag.push(element.id);
    });
  }

  /**
   * Convenience method for passing on context parameters
   * to table definition creation factory
   */
  protected getTableDefinitionContext(): any {
    return null;
  }

  /**
   * Runs after selection changes and marks the first and last rows
   * of a selection band. Used by cell class rules to render along with row selection
   * class
   *
   * _will not be populated unless by subclass conducting some preprocessing
   */
  public onSelectionChanged(selected?: RowNode[] | null): void {
    BfxBaseBlotter.gridSelectionChanged(this.gridOptions, selected);
  }

  public static gridSelectionChanged(gridOptions: GridOptions, selected?: RowNode[] | null): void {
    selected = selected ?? gridOptions.api?.getSelectedNodes();

    if (selected && selected.length > 0) {
      let lastRowIndex: number = null;
      let lastIndex: number = null;

      selected
        .sort((a, b) => a.rowIndex - b.rowIndex)
        .forEach((r, index) => {
          if (lastRowIndex == null || lastRowIndex < r.rowIndex - 1) {
            if (lastRowIndex != null) {
              selected[lastIndex][ROW_SELECTION_MARKER].push(ROW_SELECTION_LAST_CLASS);
            }
            r[ROW_SELECTION_MARKER] = [ROW_SELECTION_FIRST_CLASS];
          } else {
            r[ROW_SELECTION_MARKER] = [];
          }

          lastRowIndex = r.rowIndex;
          lastIndex = index;
        });

      if (
        lastRowIndex != null &&
        selected[lastIndex] &&
        selected[lastIndex][ROW_SELECTION_MARKER]
      ) {
        selected[lastIndex][ROW_SELECTION_MARKER].push(ROW_SELECTION_LAST_CLASS);
      }

      gridOptions.api.refreshCells({ rowNodes: selected, force: true });
    }
  }

  protected updateSelectionHeader(): void {
    if (this.gridOptions.api) {
      let col = this.gridOptions.columnApi.getColumn("selection");
      let colDef;
      if (col) {
        colDef = col.getColDef();
        if (col == null || colDef == null) {
          logger.out.warn(`${this.constructor.name}.updateSelectionHeader() failed to get column`);
          this.gridOptions.api.refreshHeader();
          return;
        }

        colDef.headerName = "(" + this.selectedRowsLength() + ")";
        colDef.headerTooltip = colDef.headerName;

        this.gridOptions.api.refreshHeader();
        clearTimeout(this.myHeaderSelectionTimer);
        this.myHeaderSelectionTimer = undefined;
      }

      this.selectedIdsCount = this.gridOptions.api.getSelectedRows().length;
    }
  }

  public onMouseUp(event: MouseEvent) {
    setImmediate(() => {
      if (this.doingDragSelect) {
        this.doingDragSelect = false;
        // for (let index = Math.min(this.firstRowToSelect,this.lastRowToSelect); index < Math.max(this.firstRowToSelect,this.lastRowToSelect); index++) {
        // 	this.gridOptions.api.selectIndex(index,true,false);

        // }
        this.pendingFinishActions.forEach((node) => {
          this.onRowSelected({ node: node });
        });
        this.pendingFinishActions.clear();
      }

      const path = getPath(event.target);
      if (
        event.button === 0 &&
        path.every((target) => !target.classList.contains("ag-selection-checkbox")) &&
        path.some((target) => target.classList.contains("ag-cell"))
      ) {
        const node = this.mouseOverCellEvent != null ? this.mouseOverCellEvent.node : null;
        let count: number = 1;

        if (node != null) {
          if (this.nodeMouseUpCountMap.has(node)) count = this.nodeMouseUpCountMap.get(node) + 1;

          this.nodeMouseUpCountMap.set(node, count);

          setTimeout(() => {
            if (this.nodeMouseUpCountMap.has(node)) {
              let count = this.nodeMouseUpCountMap.get(node) - 1;
              if (count > 0) this.nodeMouseUpCountMap.set(node, count);
              else this.nodeMouseUpCountMap.delete(node);
            }
          }, CELL_DOUBLE_CLICK_THRESHOLD);
        }

        if (count === 2) {
          this.onCellDoubleClicked(this.mouseOverCellEvent);
        }
      }

      if (event.button === 0 && this.contextMenuService?.hideMenu) {
        try {
          this.contextMenuService.hideMenu(this);
        } catch (e) {
          logger.out.warn(
            `${this.constructor.name}: Failed to invoke hide menu on` + ` blotter mouse up: ${e}`,
            e,
          );
        }
      }
    });
  }

  protected initializeColumnDefinitions(): void {
    this.gridOptions.onGridSizeChanged = () => {
      try {
        this._agGridResetScroll();
      } catch (e) {
        logger.out.warn(
          `${this.constructor.name}.onGridSizeChanged ag-grid private API use failed.`,
        );
      }
    };

    this.gridOptions.onSelectionChanged = () => {
      this.subjectRefreshHeader.next();
      this.onSelectionChanged();
    };

    this.gridOptions.onCellContextMenu = (event) => {
      if (this.embeddedTableMode && !this.embeddedTableModeContextMenu) {
        return;
      }

      if (this.gridOptions.api) {
        if (!event.node.isSelected()) {
          this.onContextMenuSelectRow(event);
        }
        let selectedItems = this.getSelectedRows();
        let selectedIds: Array<string> = [];

        selectedItems.map((order) => {
          if (this.tableDefinition.hasOverwriteKey()) {
            selectedIds.push(order[this.tableDefinition.overwriteKey].toString());
          } else {
            selectedIds.push(order[this.tableDefinition.primaryKey].toString());
          }
        });
        let mouseEvent: MouseEvent = event.event as MouseEvent;
        this.contextMenuService.hideMenu(this);
        this.contextMenuService.doMenu(
          this,
          mouseEvent.pageX,
          mouseEvent.pageY,
          selectedIds,
          selectedItems,
        );
      }
    };

    // if the user changes filter, either on filter bar or quick filter or column filter, then we deselect orders
    this.gridOptions.onFilterChanged = () => {
      if (!this.suppressNextFilterChangedDeselection) {
        this.gridOptions?.api?.deselectAll();
      } else {
        this.suppressNextFilterChangedDeselection = false;
      }
    };

    if (!this.embeddedTableMode) {
      this.applyColumnDefinitions();
    }
  }

  public onContextMenuSelectRow(event: CellContextMenuEvent) {
    this.gridOptions.api.deselectAll();
    event.node.setSelected(true, false);
  }

  public get contextMenuService(): BfxBaseBlotterContextMenuService<any> {
    if (this.embeddedTableModeContextMenu) {
      return this.embeddedContextMenuService;
    }

    return this._contextMenuService;
  }

  public onKeyPress(event) {
    //add keyboard navigation here
    // logger.out.info("blotter key : "+event.key);
    // let idx = -1;
    // let poistion:string;
    // switch(event.key){
    // 	case "ArrowUp":
    // 		poistion="top";
    // 		idx = Math.max(this.gridOptions.api.getFirstRenderedRow()-1,0);
    // 		break;
    // 	case "ArrowDown":
    // 		idx = Math.min(this.gridOptions.api.getLastRenderedRow()+1,this.gridOptions.api.getModel()['rootNode'].childrenAfterFilter.length-1);
    // 		poistion="bottom";
    // 		break;
    // 	case "PageUp":
    // 		idx = Math.max(this.gridOptions.api.getFirstRenderedRow()-1,0);
    // 		poistion="bottom";
    // 		break;
    // 	case "PageDown":
    // 		idx = this.gridOptions.api.getLastRenderedRow();
    // 		poistion="top";
    // 		break;
    // 	case "Home":
    // 		idx = 0;
    // 		poistion="top";
    // 		break;
    // 	case "End":
    // 		idx = this.gridOptions.api.getModel()['rootNode'].childrenAfterFilter.length-1;
    // 		poistion="bottom";
    // 		break;
    // }
    // if(idx>=0){
    // 	this.gridOptions.api.ensureIndexVisible(idx,poistion);
    // 	// grid is losing focus, so onKeyPress is only getting called once, this doesnt work eiter
    // 	setTimeout(() => {
    // 		this.gridOptions.api.setFocusedCell(idx,"selection");
    // 	}, 100);
    // }
  }

  /**
   * Returns the row items currently selected aside from the ones
   * that have IDs filtered out by the isValidRowID method
   */
  protected getSelectedRows(): Array<any> {
    let selectedItems = this.gridOptions.api.getSelectedRows();

    if (selectedItems != null && selectedItems.length > 0) {
      selectedItems = selectedItems.filter((row: any) => {
        let key = this.tableDefinition.hasOverwriteKey()
          ? row[this.tableDefinition.overwriteKey]
          : row[this.tableDefinition.primaryKey];
        return key != null && this.isValidRowID(key);
      });
    }

    return selectedItems;
  }

  /**
   * Get all rows in their current display order
   * @returns
   */
  public getAllRows(): any[] {
    let rowData = [];
    this.gridOptions?.api.forEachNodeAfterFilterAndSort((node) => rowData.push(node.data));
    return rowData;
  }

  /**
   * public accessor of ***getSelectedRows()*** protected method
   *
   * Returns the row items currently selected aside from the ones
   * that have IDs filtered out by the isValidRowID method
   */
  public getSelectedTableRows(): Array<any> {
    return this.getSelectedRows();
  }

  /**
   * Indicates if the parameter sent is a valid row ID for this blotter. Invalid
   * ones are filtered out from the selection segment provided by the getSelectedRows method
   * @param rowId the rows ID to be checked
   */
  protected isValidRowID(rowId: string): boolean {
    return true;
  }

  protected applyColumnDefinition(column: BfxColDef): any {
    let col: BfxColDef = Object.assign({}, column);
    let colConfig = null;

    if (this.columnsConfiguration) colConfig = this.columnsConfiguration[col.field];

    col.lockPinned = true;

    if (column.searchKey != null) {
      col.getQuickFilterText = function (params) {
        if (params.column.isVisible()) {
          let myRetrun = column.hasSearchKey ? params.data[column.searchKey] : "";
          return myRetrun;
        }
        return "";
      };
    }

    if (column.width != null) {
      if (colConfig != null && colConfig != undefined && colConfig.width != undefined) {
        if (col.maxWidth < colConfig.width) {
          col.width = col.maxWidth;
        } else {
          col.width = colConfig.width;
        }
      }
    }
    if (col.minWidth > 0 && col.width < col.minWidth) {
      col.width = col.minWidth;
    }

    if (col.cellClass == undefined || col.cellClass == null || col.cellClass.length == 0) {
      col.cellClass = [DEFAULT_CELL_CLASS];
    }

    BfxBaseBlotter.applySelectionCellRules(col);

    if (colConfig != null && colConfig != undefined && colConfig.visible != undefined)
      col.hide = !colConfig.visible;

    if (colConfig != null && colConfig != undefined && colConfig.position != undefined)
      col.position = colConfig.position;

    col.headerTooltip = column.headerTooltip || column.headerName;

    this.applyTooltipValueGetter(col);

    this.applyComparator(col, column);

    return col;
  }

  public static applySelectionCellRules(col: ColDef): void {
    if (col.cellClassRules == null) {
      col.cellClassRules = {};
    }

    // First and last row of selection cell classes
    col.cellClassRules[ROW_SELECTION_FIRST_CLASS] = (params: CellClassParams) => {
      if (params.node != null && params.node[ROW_SELECTION_MARKER] != null) {
        return params.node[ROW_SELECTION_MARKER].includes(ROW_SELECTION_FIRST_CLASS);
      }
      return false;
    };

    col.cellClassRules[ROW_SELECTION_LAST_CLASS] = (params: CellClassParams) => {
      if (params.node != null && params.node[ROW_SELECTION_MARKER] != null) {
        return params.node[ROW_SELECTION_MARKER].includes(ROW_SELECTION_LAST_CLASS);
      }
      return false;
    };
  }

  protected applyColumnDefinitions(): void {
    let defs: Array<BfxColDef> = this.tableDefinition.columns.concat();
    defs = defs.map((def) => {
      return this.applyColumnDefinition(def);
    });

    if (this.embeddedTableMode && this.embeddedTableModeSuppressSelection === true) {
      defs.splice(0, 1);
      this.gridOptions.rowSelection = "single";
    }

    defs.sort((a, b) => {
      return a.position - b.position;
    });

    //set explicit colId to avoid ag-grid post fixing
    // _1, _n for unqiueness in embedded mode
    defs.forEach((def) => (def.colId = def.field));

    // Make sure columns that must be visible in embedded mode and cannot be enabled/disabled from column picker are visible in embedded mode
    defs.forEach((def) => {
      if (
        this.embeddedTableMode === true &&
        def &&
        def.visibleInEmbeddedMode === true &&
        def.disableInPicker === true
      ) {
        def.hide = false;
      }

      if (this.embeddedTableMode === false && def && def.disableInPicker === true) {
        def.hide = false;
      }
    });

    this.columnDefs = defs;
  }

  protected applyTooltipValueGetter(
    col: BfxColDef,
    tooltipValueGetter = GenericTooltipValueGetter,
  ): void {
    if (col.tooltipValueGetter == null && col.showTooltip !== false) {
      col.tooltipValueGetter = tooltipValueGetter;
    }
  }

  protected applyComparator(col: any, column: BfxColDef): void {
    let columnName: string = column.sortKey ? column.sortKey : column.field;

    switch (column.indexType) {
      case IndexType.Boolean:
      case IndexType.String:
        col.comparator = this.sortString.bind(this, columnName);
        break;
      case IndexType.Double:
      case IndexType.Integer:
        col.comparator = this.sortNumber.bind(this, columnName);
        break;
      case IndexType.Long:
        col.comparator = this.sortLong.bind(this, columnName);
        break;
      case IndexType.LadderTracking:
        col.comparator = SORT_LADDER_TRACKING;
        break;
      case IndexType.PercAway:
        col.comparator = SORT_PERC_AWAY;
        break;
      case IndexType.NaturalString:
        //col.comparator = this.sortBigNumber.bind(this, columnName);
        col.comparator = this.sortNumber.bind(this, columnName);
        break;
      case IndexType.StrikethroughRate:
        col.comparator = this.sortStrikethroughRate.bind(this, columnName);
        break;
      case IndexType.FixingDate:
        col.comparator = this.sortFixingDate.bind(this, columnName);
        break;
      case IndexType.Instrument:
        col.comparator = SORT_INSTRUMENT;
        break;
      case IndexType.AgGridDefault:
      default:
        // leave col.comparator alone!
        break;
    }
  }

  protected sortString(columnName: string, valueA: string, valueB: string, nodeA, nodeB): number {
    let strA: string = valueA;
    if (!strA) strA = "";
    let strB: string = valueB;
    if (!strB) strB = "";
    return strA.localeCompare(strB);
  }

  /**
   * Sort numbers - nulls/undefined always sort before actual numbers
   * @param columnName
   * @param valueA
   * @param valueB
   * @param nodeA
   * @param nodeB
   * @returns
   */
  protected sortNumber(columnName: string, valueA: number, valueB: number, nodeA, nodeB): number {
    const n1 = valueA !== null && valueA !== undefined ? Number(valueA) : NaN;
    const n2 = valueB !== null && valueB !== undefined ? Number(valueB) : NaN;

    if (isNaN(n1)) return isNaN(n2) ? 0 : -1;
    if (isNaN(n2)) return 1;

    return n1 - n2;
  }

  protected sortStrikethroughRate(
    columnName: string,
    valueA: any,
    valueB: any,
    nodeA,
    nodeB,
  ): number {
    const v1 = typeof valueA === "string" ? PricingService.clearStrikeThroughValue(valueA) : valueA;
    const v2 = typeof valueB === "string" ? PricingService.clearStrikeThroughValue(valueB) : valueB;

    const n1 = v1 !== null && v1 !== undefined ? Number(v1) : NaN;
    const n2 = v2 !== null && v2 !== undefined ? Number(v2) : NaN;

    if (isNaN(n1)) return isNaN(n2) ? 0 : -1;
    if (isNaN(n2)) return 1;

    return n1 - n2;
  }

  protected sortLong(
    columnName: string,
    valueA: CubiqTS.Long,
    valueB: CubiqTS.Long,
    nodeA,
    nodeB,
  ): number {
    return nodeA.data[columnName].compare(nodeB.data[columnName]);
  }

  protected sortFixingDate(columnName: string, valueA: string, valueB: string, nodeA, nodeB) {
    let fixingDateTimestamp1 = this.myFixingDateHelper.getTimestamp(
      nodeA.data,
      COLUMN_ORDER_FIXING_DATE,
    );
    let fixingDateTimestamp2 = this.myFixingDateHelper.getTimestamp(
      nodeB.data,
      COLUMN_ORDER_FIXING_DATE,
    );
    return fixingDateTimestamp1 - fixingDateTimestamp2;
  }

  protected renderCell(params): string {
    return params.value != null ? params.value : "";
  }

  protected renderCellWithToooltip(params) {
    if (params.value != null) {
      return `<span class="blotter__comment_cell" title="${params.value}">${params.value}</span>`;
    }
    return "";
  }

  protected renderEmpty(): string {
    return "";
  }

  protected initializeInMemory(): void {
    this.service.isReady().then(() => {
      let rowData: Array<any> = this.service.getRowData();
      this.gridOptions.api.setRowData(rowData);
      this.onAdaptedDataPrimed();
      this.blotterReady();
    });

    this.fullUpdateSubscription = this.service.fullUpdateSubject.subscribe(
      this.onFullUpdateEvent.bind(this),
    );
    this.forceUpdateSubscription = this.service.forceUpdateSubject.subscribe(
      this.onForceUpdateEvent.bind(this),
    );
    this.deleteSubscription = this.service.deleteItemSubject.subscribe(
      this.onDeleteEvent.bind(this),
    );

    this.gridOptions.isExternalFilterPresent = this.isExternalFilterPresent.bind(this);
    this.gridOptions.doesExternalFilterPass = this.doesExternalFilterPass.bind(this);
    this.defaultSortingModel = this._tableDefinitionFactory.getDefaultSortingModel();
  }

  private initializeInMemoryCompleted() {
    if (this.defaultSortingModel != null && this.defaultSortingModel.length > 0) {
      this.gridOptions.api.setSortModel(this.defaultSortingModel);
    }
    this.filterSelectionChanged(this.filterConfiguration);
    this.restoreSortFromConfig();
  }

  public clearEmbeddedTable(): void {
    if (this.tableDefinition != null && this.gridOptions != null && this.gridOptions.api != null) {
      this.gridOptions.api.setRowData([]);
    }
  }

  public setEmbeddedTableLoadPending(): void {
    this.embeddedTablePreloadData = null;
    this.clearEmbeddedTable();
    if (this.gridOptions && this.gridOptions.api) this.gridOptions.api.showLoadingOverlay();
  }

  // TODO consider renaming setEmbeddedTable
  public setEmbeddedTable(
    rowData: Array<any>,
    config?: any,
    allowListOverrideConfig?: Map<string, object>,
    checkPrimaryPriority: boolean = false,
  ): void {
    if (this.tableDefinition == null || this.gridOptions == null || this.gridOptions.api == null) {
      this.embeddedTablePreloadData = {
        rowData: rowData,
        config: config,
        allowListOverrideConfig: allowListOverrideConfig,
        checkPrimaryPriority: checkPrimaryPriority,
      };
      logger.out.info(
        `${this.constructor.name}.setEmbeddedTable break early after setting embeddedTablePreloadData`,
      );
      return;
    } else {
      this.embeddedTablePreloadData = null;
    }

    this.defaultSortingModel = this._tableDefinitionFactory.getDefaultSortingModel();

    if (allowListOverrideConfig) {
      this.tableDefinition.columns.forEach((column) => {
        if (
          allowListOverrideConfig.has(column.field) &&
          this.tableDefinition.columns[this.tableDefinition.columnIndexMap.get(column.field)]
        ) {
          this.tableDefinition.columns[this.tableDefinition.columnIndexMap.get(column.field)] =
            Object.assign(
              {},
              this.tableDefinition.columns[this.tableDefinition.columnIndexMap.get(column.field)],
              allowListOverrideConfig.get(column.field),
            );
        } else if (
          this.embeddedHideUnconfigured &&
          this.tableDefinition.columns[this.tableDefinition.columnIndexMap.get(column.field)]
        ) {
          // not displayable or configuratable
          this.tableDefinition.columns[this.tableDefinition.columnIndexMap.get(column.field)].hide =
            true;
          this.tableDefinition.columns[
            this.tableDefinition.columnIndexMap.get(column.field)
          ].omitFromPicker = true;

          if (config && config.columnsConfiguration && config.columnsConfiguration[column.field]) {
            config.columnsConfiguration[column.field] = Object.assign(
              {},
              config.columnsConfiguration[column.field],
              {
                visible: false,
                omitFromPicker: true,
              },
            );
          }
        }
      });
    }

    if (config) {
      this.restoreFromConfiguration(config);
    }

    this.applyColumnDefinitions();

    rowData = this.filterDuplicateRowData(rowData);

    logger.out.info(`Blotter: Populating with ${rowData.length} rows`);
    this.gridOptions.api.setRowData(rowData);
  }

  protected filterDuplicateRowData(rowData: any[]): any[] {
    let dedup = new Map();

    rowData.forEach((v) => {
      let key = v[this.tableDefinition.primaryKey].toString();
      dedup.set(key, v);
    });

    return [...dedup.values()];
  }

  protected blotterReady(): void {}

  protected getItemId(row: any): string {
    return row != null
      ? this.tableDefinition.hasOverwriteKey()
        ? row[this.tableDefinition.overwriteKey]
        : row[this.tableDefinition.primaryKey]
      : null;
  }

  protected areUpdatesFrozen(): boolean {
    // default to not updating an embedded table.
    // HOM mode hedge orders blotter is a notable exception
    return this.embeddedTableMode;
  }
  // overide to perform any actions after data for the blotter is primed
  protected onAdaptedDataPrimed(): void {
    this.primed = true;
  }

  ngOnDestroy() {
    if (this.fullUpdateSubscription) {
      this.fullUpdateSubscription.unsubscribe();
    }

    if (this.deleteSubscription) {
      this.deleteSubscription.unsubscribe();
    }
  }

  protected addListenersToTable(): void {}

  resize() {
    if (this.viewInitComplete == false) return;

    this.height =
      this.topMenuView != null
        ? this.topMenuView.nativeElement.parentElement.clientHeight - 40
        : 500;

    if (this.blotterFilterService) {
      this.blotterFilterService.onResize();
    }
  }

  public showFilterManager(event: Event): void {
    BfxBlotterFilterManagerComponent.openFilterManager(
      this._tableDefinitionFactory,
      this.translationService,
      this.layoutService,
      FilterType.BlotterFilter,
      this.blotterId,
      event,
    );
  }

  public toggleSearch(el?: any): void {
    this.showSearch = !this.showSearch;
    if (!this.showSearch) {
      this.searchValue = "";
      this.onChangeSearchValue();
    }
  }

  public resizeScrollBarForData() {}

  public ngAfterViewInit(): void {
    this.viewInitComplete = true;
  }

  public onChangeSearchValue() {
    this.search.next();
  }

  public isExternalFilterPresent(): boolean {
    return this.blotterFilterBar != null && this.blotterFilterBar.getSelectedItems().length > 0;
  }

  public doesExternalFilterPass(node: RowNode): boolean {
    let ret: boolean = true;
    if (this.filteringService && this.blotterFilterBar) {
      let filters: Array<BfxBlotterFilter> = this.blotterFilterBar.getSelectedFilters();

      try {
        if (filters != null && filters.length > 0) {
          ret = this.filteringService.doFiltersPass(node, filters);
        }
      } catch (error) {
        logger.out.error("Error while doing a filter pass.", error);
      }
    }

    return ret;
  }

  protected getBlotterVersion(): string {
    return this.blotterId + ":" + "v" + this._tableDefinitionFactory.getVersionIdentifier();
  }

  public getComponentConfiguration(): any {
    return {
      configVersion: this.getBlotterVersion(),
      filterConfiguration: this.filterConfiguration,
      blotterId: this.blotterId,
      columnsConfiguration: this.getBlotterColumnsConfigs(),
      sortingModel: this.getBlotterSortingModel(),
    };
  }

  public migrateFromConfiguration(config: any): any {
    return config;
  }

  public restoreFromConfiguration(config: any): void {
    config.filterConfiguration = this.blotterFilterService.migrateFilterConfiguration(
      config.filterConfiguration,
    );
    this.filterConfiguration = config.filterConfiguration;

    if (this.filterConfiguration == null) {
      this.filterConfiguration = [];
    }

    if (this.blotterFilterService) {
      this.blotterFilterService.applyFilterConfig({
        blotterId: this.blotterId,
        items: this.filterConfiguration,
      });
    }

    if (this.getBlotterVersion() == config.configVersion) {
      this.columnsConfiguration = config.columnsConfiguration;
      this.sortingModel = config.sortingModel;
    }
  }

  public getBlotterId(): string {
    return this.blotterId;
  }

  public getBlotterInstance(): any {
    return this.blotterInstance;
  }

  public getBlotterTypeStr(): string {
    return CubiqTS.BlotterType[this.getBlotterType()];
  }

  public abstract getBlotterType(): CubiqTS.BlotterType;

  public getProfileSettingKey(): string {
    return `${ProfileSettingKeys.userProfileDefaultBlotter}${this.getBlotterTypeStr()}`;
  }

  public getFilterConfiguration(): any {
    return this.filterConfiguration;
  }

  public setBlotterFilterComponent(blotterFilterBar: BfxBlotterFilterBarComponent): void {
    this.blotterFilterBar = blotterFilterBar;
  }

  public filterSelectionChanged(filterConfiguration: FilterConfiguration): void {
    this.filterConfiguration = filterConfiguration;
    if (this.gridOptions.api) {
      this.gridOptions.api.onFilterChanged();
      this.afterFilteringCompleted();
    }
  }

  protected afterFilteringCompleted(): void {
    this.gridDataUpdated();
  }

  protected checkForNoRows(): void {}

  public onBodyScroll(): void {}

  public showColumnSelector(event): void {
    this.columnSelectorService.doColumnSelectorMenu(this, this.gridOptions, event);
  }

  // handle the click of the ag-grid column header and header
  public handleClick(event) {
    if (event.which == 3 && event.target.className.indexOf("ag-header-cell") !== -1) {
      this.showColumnSelector(event);
    } else if (event.which == 3 && event.target.className.indexOf("ag-header-container") !== -1) {
      this.columnSelectorService.doColumnSelectorMenu(this, this.gridOptions, event);
    }
  }

  /**
   *
   * due to trackpad driver interactions - ag-grid's (cellDoubleClick) output is unsafe.
   * see BfxBaseBlotter.onMouseUp() for source of call to this method.
   */
  public onCellDoubleClicked(_: CellEvent) {
    // binding for template. please override in subclass
  }

  private getBlotterColumnsConfigs(): any {
    if (
      this.gridOptions == null ||
      this.gridOptions.columnApi == null ||
      typeof this.gridOptions.columnApi.getAllGridColumns !== "function"
    ) {
      logger.out.error(
        `${this.constructor.name}.saveAsDefault() failed ag-grid gridOptions are invalid`,
      );
      // column config won't be restored but defaults will still apply
      return null;
    }

    let columnsToSave = {};
    let positionCounter = 0;
    this.gridOptions.columnApi.getAllGridColumns().forEach((gridCol) => {
      let column = {
        id: "",
        width: 150,
        position: 1,
        visible: true,
        isPrimary: false,
        isPriority: false,
      };
      column.id = gridCol.getColId();
      column.width = gridCol.getActualWidth();
      column.visible = gridCol.isVisible();
      column.position = positionCounter;
      let def = gridCol.getColDef();
      if (def.cellClass) {
        if (def.cellClass instanceof Array || def.cellClass instanceof String) {
          column.isPrimary = def.cellClass.indexOf("blotter_primary_col") > -1;
          column.isPriority = def.cellClass.indexOf("blotter_priority_col") > -1;
        }
      }

      columnsToSave[gridCol.getColId()] = column;
      positionCounter++;
    });
    this.columnsConfiguration = columnsToSave;

    return columnsToSave;
  }

  //this can be moved to base once the ag grid has being implemented
  private getBlotterSortingModel(): Array<any> {
    if (
      this.gridOptions == null ||
      this.gridOptions.api == null ||
      typeof this.gridOptions.api.getSortModel !== "function"
    ) {
      logger.out.error(
        `${this.constructor.name}.saveAsDefault() failed ag-grid gridOptions are invalid`,
      );
      // sort model won't be restored but defaults will still apply
      return null;
    }

    this.sortingModel = this.gridOptions.api.getSortModel();
    return this.sortingModel;
  }

  protected isAllSelected(): boolean {
    return (
      this.gridOptions.api.getModel()["rootNode"].childrenAfterFilter.length ===
      this.gridOptions.api.getSelectedRows().length
    );
  }

  //this can be moved to base once the ag grid has being implemented
  public restoreSortFromConfig(): void {
    if (this.gridOptions.api && this.sortingModel) {
      this.gridOptions.api.setSortModel(this.sortingModel);
    }
  }

  protected getConfigToSaveAsDefault(): any {
    return {
      version: this._tableDefinitionFactory.getVersionIdentifier(),
      columnsConfiguration: this.getBlotterColumnsConfigs(),
      sortingModel: this.getBlotterSortingModel(),
    };
  }

  public saveAsDefault(): void {
    if (
      this.gridOptions == null ||
      this.gridOptions.columnApi == null ||
      typeof this.gridOptions.columnApi.getAllGridColumns !== "function"
    ) {
      logger.out.error(
        `${this.constructor.name}.saveAsDefault() failed ag-grid gridOptions are invalid`,
      );
      return;
    }

    let profileSettings: Array<CubiqTS.ProfileSettingImpl> = [];
    profileSettings.push(
      this.userProfileService.createProfileSettingObject(
        this.getProfileSettingKey(),
        JSON.stringify(this.getConfigToSaveAsDefault()),
      ),
    );
    this.profileActionService.updateProfile(profileSettings, false).then(
      () => {
        this.saveProfileNotificationService.notifySuccessOnFooter(PROFILE_TYPE.BLOTTER_LAYOUT);
        return;
      },
      (reason) => {
        logger.out.warn(`${this.constructor.name}.saveAsDefault() failed ${reason}`);
        this.saveProfileNotificationService.notifyFailureWithPopup(PROFILE_TYPE.BLOTTER_LAYOUT);
      },
    );
  }

  protected onFullUpdateEvent(rows: Array<any>): void {
    this.updateGridData();
  }

  protected onForceUpdateEvent(rows: Array<any>): void {
    this.updateGridData({
      redrawRows: true,
      immediate: Array.isArray(rows) ? rows.length === 0 : false,
    });
  }

  protected onDeleteEvent(rowId: any): void {
    this.updateGridData();
  }

  public updateGridData(
    opts: {
      redrawRows?: boolean;
      immediate?: boolean;
    } = {},
  ): void {
    if (!this.scheduledRefresh || opts.immediate) {
      clearTimeout(this.scheduledRefresh);

      this.scheduledRefresh = setTimeout(
        () => {
          if (
            this.tableDefinition != null &&
            this.gridOptions != null &&
            this.gridOptions.api != null
          ) {
            const rowData = this.service.getRowData();
            const rowCount = rowData?.length ?? 0;
            logger.out.info(
              `${this.constructor.name} - setting row model - rowCount = ${rowCount}`,
            );

            this.gridOptions.api.setRowData(rowData);

            if (opts.redrawRows) {
              this.gridOptions.api.redrawRows();
            }
          } else if (
            this.tableDefinition != null &&
            this.gridOptions != null &&
            this.gridOptions.api == null
          ) {
            // an expected, if unfortunate condition during init especially in BM Summary
            logger.out.info(`${this.constructor.name}.updateGridData() ignoring early call`, opts);
          } else {
            logger.out.warn(`${this.constructor.name}.updateGridData(%O) failed`, opts);
          }
          this.gridDataUpdated();
          this.scheduledRefresh = null;
        },
        opts.immediate ? 0 : 200,
      );
    }
  }

  protected gridDataUpdated(): void {
    // this.contextMenuService.hideMenu(this);
    if (
      this.publishGridDataUpdateInfo &&
      this.gridOptions != null &&
      this.gridOptions.api != null
    ) {
      this.gridDataUpdatedSubject.next({
        displayedRowCount: this.gridOptions.api.getDisplayedRowCount(),
        rowCount: this.service.getRowData().length,
      });
    }
  }

  protected doResortOnPartialUpdate(): boolean {
    return false;
  }

  protected selectedRowsLength(): number {
    return this.gridOptions.api.getSelectedRows().length;
  }

  public shouldShowTemperatureFilter(): boolean {
    return false;
  }

  public subscribeToSearch(): void {
    this.search = new EventEmitter<string>();
    this.search.subscribe(() => {
      this.updateSearch();
    });
  }

  protected updateSearch() {
    this.gridOptions.api.setQuickFilter(this.searchValue);
  }

  public onDropped(): void {
    try {
      this._agGridResetScroll();
    } catch (e) {
      logger.out.warn(`${this.constructor.name}.onGridSizeChanged ag-grid private API use failed.`);
    }
  }

  public hasCellRenderedSelection(): boolean {
    return false;
  }

  public refreshSelectionCells(): void {
    if (this.gridOptions && this.gridOptions.api)
      this.gridOptions.api.refreshCells({
        columns: [COLUMN_SELECTION],
        force: true,
      });
  }

  protected _agGridResetScroll(): void {
    // FIXME: use public API, if and when it is available
    // confirmed needed with ag-grid@17.0.0
    let api: any = this.gridOptions.api as any;

    if (
      api != null &&
      typeof api == "object" &&
      api.gridPanel != null &&
      typeof api.gridPanel == "object" &&
      api.gridPanel.onBodyScroll != null &&
      typeof api.gridPanel.onBodyScroll == "function"
    ) {
      api.gridPanel.onBodyScroll();

      api.refreshHeader();
      api.refreshCells({
        force: true,
      });
    }
  }

  public shouldShowStateFilter(): boolean {
    return false;
  }

  public onSetColumnVisible(_: BfxColDef) {}

  public hasPendingFinishAction(_: RowNode): boolean {
    return false;
  }

  /** not wired up by default see bfx-blotter-orders */
  protected onRowSelected(event, selectedNodes?: RowNode[]): void {}
}
